import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { FormInput } from 'components/molecules/FormInput';
import { initialShotgunDetailsStepFormValues } from 'models/forms/applicationForm';
import { yupResolver } from '@hookform/resolvers/yup';
import ApplicationStore from 'state/ApplicationStore';
import { EDictionaryTypes } from 'models/dictionary';
import {
  StyledStepTitle,
  StyledSectionTitle,
  StyledParagraph,
} from 'pages/ApplyOnlinePage/ApplyOnlineWizardPage/WizardSteps/styles';
import { useApplyWizard } from 'pages/ApplyOnlinePage/ApplyOnlineWizardPage/hooks';
import { useShotgunDetailsStepFormSchema } from './schemaValidations';

interface IShotgunDetailsStep {
  setNextStep: (arg: number) => void;
}

const ShotgunDetailsStep = ({ setNextStep }: IShotgunDetailsStep) => {
  const { schema } = useShotgunDetailsStepFormSchema();
  const { loadDictionaries, make } = useApplyWizard();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialShotgunDetailsStepFormValues,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('SLL errors', errors, make);
    ApplicationStore.setStepErrors(2, Object.keys(errors).length > 0);
  }, [errors]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.Make]);
    })();
  }, []);

  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    const handler = () => {
      if (formRef.current) {
        formRef.current.requestSubmit();
      }
    };
    document.addEventListener('submitFormEvent', handler);
    return () => {
      document.removeEventListener('submitFormEvent', handler);
    };
  }, []);

  (window as any).fireCycleStepThree = () => {
    reset({
      holdShotgun: '1',
      calibre: '6',
      actionType: 'Action Type 1',
      make: 'Smiths',
      serialNumber: 'AA646464',
      securityArrangements: '1',
      securityDetails: 'Lorem ipsum',
      securityType: 'Sec Type 01',
      securityAdditionalInformation: 'Additional info',
      certificateHolder: '1',
      certificateDetails: 'Certs details',
      ammoStorage: 'House',
    });
  };

  useEffect(() => {
    const formData = ApplicationStore.getStepData('shotgunData');
    if (Object.keys(formData).length > 0) {
      reset(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitForm = (data: any) => {
    // eslint-disable-next-line no-console
    console.log('SLL onSubmitForm ShotgunDetailsStep', data);
    ApplicationStore.setStepData('shotgunData', data);
    ApplicationStore.setStepErrors(2, false);
    setNextStep(3);
  };

  return (
    <Box>
      <StyledStepTitle variant="h4">Shotgun details</StyledStepTitle>
      <StyledSectionTitle variant="h5">Current Shotgun</StyledSectionTitle>
      <StyledParagraph variant="body2">Details of shotguns currently held.</StyledParagraph>

      <form onSubmit={handleSubmit(onSubmitForm)} ref={formRef}>
        <Box>
          <FormRadioGroup
            name="holdShotgun"
            label="Do you currently hold any shotguns? If YES, specify details"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <FormInput
                name="calibre"
                label="Calibre/Bore"
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid item xs={4}>
              <FormInput
                name="actionType"
                label="Action/Type"
                required
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid item xs={4}>
              <FormInput name="make" label="Make" required control={control} errors={errors} />
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <FormInput
                name="serialNumber"
                label="Serial Number"
                control={control}
                errors={errors}
              />
            </Grid>
          </Grid>
        </Box>

        <StyledSectionTitle variant="h5">Security Arrangements</StyledSectionTitle>
        <StyledParagraph variant="body2">
          Details of current (or proposed) security arrangements.
        </StyledParagraph>
        <StyledParagraph variant="body2">
          Please allow the police to inspect your guns and security when requested as in the
          absence of a warrant consent is required for the police to inspect premises.
        </StyledParagraph>

        <Box>
          <FormRadioGroup
            name="securityArrangements"
            label="Are the security arrangements at one address?"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />

          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormInput
                name="securityDetails"
                label="If NO, provide details"
                control={control}
                errors={errors}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1}>
            <Grid item xs={4}>
              <FormInput
                name="securityType"
                label="Type of Security"
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid item xs={8}>
              <FormInput
                name="securityAdditionalInformation"
                label="Additional Information"
                control={control}
                errors={errors}
              />
            </Grid>
          </Grid>

          <FormRadioGroup
            name="certificateHolder"
            label="Is the security shared with another certificate holder?"
            options={[
              { label: 'YES', value: '1' },
              { label: 'NO', value: '0' },
            ]}
            control={control}
            errors={errors}
          />

          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormInput
                name="certificateDetails"
                label="If YES, provide details"
                control={control}
                errors={errors}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormInput
                name="ammoStorage"
                label="Ammunition Storage - Please provide details"
                control={control}
                errors={errors}
              />
            </Grid>
          </Grid>
        </Box>
      </form>
    </Box>
  );
};

export default ShotgunDetailsStep;
